import { useCallback, useEffect, useRef, useState } from 'react';
import { useAPI } from '../contexts/APIContext';
import { fetchDubs, rememberAudioLanguage, requestDub } from '../lib/muxStreaming';

const POLL_MS = 5000;

/**
 * Dub languages for a story on Mux: which are ready, being made, or can be
 * asked for. While one is being made it polls; when it becomes ready it
 * calls onReady(lang) (the player reloads the stream and switches to it).
 */
export function useDubs(contentId, { shareToken = null, enabled = true, onReady } = {}) {
  const { apiClient } = useAPI();
  const [data, setData] = useState({ languages: [], can_request: false, my_active: null });
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const waitingFor = useRef(new Set()); // languages this viewer asked for

  const load = useCallback(async () => {
    if (!enabled || !contentId) return null;
    const next = await fetchDubs(apiClient, contentId, shareToken);
    if (!next) return null;
    setData(next);
    for (const lang of Array.from(waitingFor.current)) {
      const item = (next.languages || []).find((l) => l.code === lang);
      if (item?.status === 'ready') {
        waitingFor.current.delete(lang);
        onReadyRef.current?.(lang);
      } else if (item?.status === 'failed') {
        waitingFor.current.delete(lang);
        setError("That dub didn't work. Please try again later.");
      }
    }
    return next;
  }, [apiClient, contentId, shareToken, enabled]);

  useEffect(() => {
    setData({ languages: [], can_request: false, my_active: null });
    setError('');
    waitingFor.current = new Set();
    load();
  }, [load]);

  const busy = (data.languages || []).some((l) => l.status === 'queued' || l.status === 'processing');
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [busy, load]);

  const request = useCallback(
    async (lang) => {
      setError('');
      setRequesting(lang);
      try {
        const res = await requestDub(apiClient, contentId, lang, shareToken);
        if (res.state === 'ready') {
          rememberAudioLanguage(lang);
          onReadyRef.current?.(lang);
        } else {
          waitingFor.current.add(lang);
        }
        await load();
      } catch (e) {
        setError(e?.message || "Couldn't start that dub. Please try again.");
      } finally {
        setRequesting(null);
      }
    },
    [apiClient, contentId, shareToken, load]
  );

  return { ...data, error, requesting, request };
}

export default useDubs;
